import { useQuery, type FetchResult } from "@apollo/client";
import {
  CNC_TASK_LAUNCHER,
  CNC_TASKS,
  type CncTask,
  type CncTaskLauncherData,
  type CncTaskParameter,
  type CncTasksData,
} from "./LaunchTaskApi";
import "./LaunchTask.scss";
import React, { useContext, useEffect, useState } from "react";
import {
  SkeletonLoader,
  TextButton,
  SelectInput,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
  AlertIndicator,
  alertTypes,
} from "@SeemplicityDev/shared";
import { Controller, useForm, type FieldError } from "react-hook-form";
import GenericField from "../../Ticketing/GenericField/GenericField";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import { fieldTypeOptions } from "@SeemplicityDev/ticket-master";

interface LaunchTaskProps {
  accountUuid: string;
}

interface TaskOption {
  label: string;
  value: string;
  description: string;
  parameters: CncTaskParameter[];
}

interface LaunchTaskFormData {
  task_name: TaskOption;
  params?: Record<string, unknown>;
}

const LaunchTask: React.FC<LaunchTaskProps> = ({ accountUuid }) => {
  const methods = useForm<LaunchTaskFormData>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = methods;
  const { data: tasksData } = useQuery<CncTasksData>(CNC_TASKS);
  const [launchTask] =
    useMutationWrapper<CncTaskLauncherData>(CNC_TASK_LAUNCHER);
  const [formattedTasksList, setFormattedTasksList] = useState<TaskOption[]>();
  const [selectedTaskParams, setSelectedTaskParams] =
    useState<CncTaskParameter[]>();
  const [selectedTaskDescription, setSelectedTaskDescription] =
    useState<string>();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { closeModal } = useContext(ModalContext);

  useEffect(() => {
    if (!!tasksData) {
      const formattedTasks = tasksData.cnc_tasks?.map((task: CncTask) => {
        return {
          label: task.task_name,
          value: task.task_name,
          description: task.task_description,
          parameters: task.task_parameters,
        };
      });

      setFormattedTasksList(formattedTasks);
    }
  }, [tasksData]);

  const onSubmit = (formData: LaunchTaskFormData) => {
    setIsSubmittingForm(true);

    launchTask({
      variables: {
        account_uuid: accountUuid,
        task_name: formData.task_name.value,
        task_parameters: JSON.stringify({ ...formData.params }),
      },
    })
      .then((res: FetchResult<CncTaskLauncherData>) => {
        showToast({ message: "Task launched successfully!" });
        closeModal();
      })
      .catch((e: { message?: string }) => {
        showErrorDialog({ message: e.message });
      })
      .finally(() => {
        setIsSubmittingForm(false);
      });
  };

  return (
    <div className={"launch-task-form-wrapper"}>
      {!!formattedTasksList ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name={"task_name"}
            render={({ field: { onChange, onBlur, value } }) => (
              <SelectInput
                data-testid={"task_name"}
                name={"task_name"}
                value={value}
                onBlur={onBlur}
                labelTop
                onChange={(selectedTask: TaskOption) => {
                  setSelectedTaskParams(selectedTask.parameters);
                  setSelectedTaskDescription(selectedTask.description);
                  onChange(selectedTask);
                }}
                error={errors?.task_name ?? undefined}
                placeholder={"Select a task to launch"}
                isSearchable
                label={"Tasks"}
                required
                options={formattedTasksList}
              />
            )}
            control={control}
            rules={{ required: REQUIRED_ERROR_MESSAGE }}
          />
          {selectedTaskDescription && (
            <>
              <h4 className={"task-params-label"}> Description </h4>
              <AlertIndicator
                type={alertTypes.INFO}
                content={selectedTaskDescription}
                wrapperClassName={"task-description"}
                hasMaxWidth={false}
              />
            </>
          )}
          {!!selectedTaskParams?.length && (
            <>
              <h4 className={"task-params-label"}> Parameters </h4>
              {selectedTaskParams.map((param: CncTaskParameter) => (
                <Controller
                  shouldUnregister={true}
                  name={`params.${param.name}`}
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <GenericField
                        {...methods}
                        classname={"task-param-field"}
                        key={param.name}
                        onChange={onChange}
                        name={param.name}
                        value={value}
                        error={
                          // `errors.fields` is not part of this form's data shape;
                          // preserved verbatim from the original JS (latent dead branch).
                          (errors as { fields?: Record<string, FieldError> })
                            ?.fields?.[param?.name]
                            ? (
                                errors as {
                                  fields?: Record<string, FieldError>;
                                }
                              )?.fields?.[param?.name]
                            : null
                        }
                        input={param.type}
                        label={`${param.name} (${param.type})`}
                        required={
                          param.type !== fieldTypeOptions.BOOLEAN &&
                          param.required
                        }
                        hideLabelHelper={false}
                        creatable={true}
                        description={param.description}
                      ></GenericField>
                    );
                  }}
                  rules={{
                    required:
                      param.type !== fieldTypeOptions.BOOLEAN && param.required
                        ? REQUIRED_ERROR_MESSAGE
                        : "",
                  }}
                ></Controller>
              ))}
            </>
          )}
          <ModalButtonsContainer>
            <TextButton
              type="button"
              data-testid={"launch-task-cancel"}
              label={"Cancel"}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                closeModal();
              }}
            />
            <TextButton
              type="submit"
              label={"Launch Task"}
              data-testid={"launch-task"}
              buttonStyle={"primary"}
              rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
              isSubmitting={isSubmittingForm}
              disabled={!isValid || !isDirty || isSubmittingForm}
            />
          </ModalButtonsContainer>
        </form>
      ) : (
        <div className={"full-height"}>
          <SkeletonLoader
            className={"launch-task-skeleton-label"}
            height={"20px"}
            width={"60px"}
          />
          <SkeletonLoader
            className={"launch-task-skeleton-input"}
            height={"24px"}
            width={"650px"}
          />
        </div>
      )}
    </div>
  );
};
export default LaunchTask;
